// Import all capability handlers
const PlanOperationsCapability = require('./PlanOperationsCapability');
const ToolManagementCapability = require('./ToolManagementCapability');
const ToolDurationCapability = require('./ToolDurationCapability');
const ToolContentModificationCapability = require('./ToolContentModificationCapability');
const BulkActionsCapability = require('./BulkActionsCapability');
const ContentEditingCapability = require('./ContentEditingCapability');
const FeatureEnablementCapability = require('./FeatureEnablementCapability');
const ProductStrategyCapability = require('./ProductStrategyCapability');
const CompetitorAnalysisCapability = require('./CompetitorAnalysisCapability');

class CapabilityRegistry {
  constructor(planStateSetters, planGetters, savePlan) {
    this.capabilities = new Map();
    this.initializeCapabilities(planStateSetters, planGetters, savePlan);
  }

  initializeCapabilities(planStateSetters, planGetters, savePlan) {
    // Tool Duration capabilities
    const toolDuration = new ToolDurationCapability(planStateSetters, planGetters, savePlan);
    this.capabilities.set('tool_duration_editing', toolDuration.handleToolDurationEditing.bind(toolDuration));

    // Universal Bulk Actions capability - REPLACES bulk_tool_duration_editing
    const BulkActionsCapability = require('./BulkActionsCapability');
    const bulkActions = new BulkActionsCapability(planStateSetters, planGetters, savePlan);
    this.capabilities.set('bulk_actions', bulkActions.handleBulkActions.bind(bulkActions));

    // Tool Management capabilities
    const toolManagement = new ToolManagementCapability(planStateSetters, planGetters, savePlan);
    this.capabilities.set('add_tools_to_stages', toolManagement.handleAddToolsToStages.bind(toolManagement));
    this.capabilities.set('remove_tools_from_stages', toolManagement.handleRemoveToolsFromStages.bind(toolManagement));

    // Tool Content Modification capability
    const ToolContentModificationCapability = require('./ToolContentModificationCapability');
    const toolContentModification = new ToolContentModificationCapability(planStateSetters, planGetters, savePlan);
    this.capabilities.set('modify_tools_content', toolContentModification.handleModifyToolsContent.bind(toolContentModification));

    // Content Editing capabilities
    const contentEditing = new ContentEditingCapability(planStateSetters, planGetters, savePlan);
    this.capabilities.set('content_editing', contentEditing.handleContentEditing.bind(contentEditing));
    this.capabilities.set('program_content_modification', contentEditing.handleProgramContentModification.bind(contentEditing));
    this.capabilities.set('bulk_content_operations', contentEditing.handleBulkContentOperations.bind(contentEditing));
    this.capabilities.set('ai_content_generation', contentEditing.handleAIContentGeneration.bind(contentEditing));

    // Feature Enablement capability
    const featureEnablement = new FeatureEnablementCapability(planStateSetters, planGetters, savePlan);
    this.capabilities.set('enable_feature', featureEnablement.handleEnableFeature.bind(featureEnablement));
    console.log(`🎯 CapabilityRegistry: Feature enablement capability registered`);

    // Plan Operations capabilities
    const planOps = new PlanOperationsCapability(planStateSetters, planGetters, savePlan);

    // Store the instance for direct access
    this.planOperationsInstance = planOps;

    // CRITICAL: Register all strategy capabilities with proper validation
    const strategyCapabilities = [
      'save_plan',
      'update_key_objectives', 
      'update_objectives',
      'update_summary',
      'update_problem_statement',
      'update_goal_statement',
      'update_target_statement',
      'update_deliverables',
      'update_metrics',
      'update_stakeholders',
      'update_programs'
    ];

    strategyCapabilities.forEach(capability => {
      const handlerMethod = capability.replace('update_', 'handleUpdate').replace(/_(.)/g, (_, letter) => letter.toUpperCase()).replace('handle', 'handle');
      const methodName = `handle${capability.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('')}`;

      if (typeof planOps[methodName] === 'function') {
        this.capabilities.set(capability, planOps[methodName].bind(planOps));
        console.log(`✅ Registered strategy capability: ${capability} -> ${methodName}`);
      } else {
        console.error(`❌ Missing handler method: ${methodName} for capability ${capability}`);
      }
    });

    // Manual registration for specific methods that don't follow the pattern
    this.capabilities.set('save_plan', planOps.handleSavePlan.bind(planOps));
    this.capabilities.set('update_key_objectives', planOps.handleUpdateKeyObjectives.bind(planOps));
    this.capabilities.set('update_objectives', planOps.handleUpdateObjectives.bind(planOps));
    this.capabilities.set('update_summary', planOps.handleUpdateSummary.bind(planOps));
    this.capabilities.set('update_problem_statement', planOps.handleUpdateProblemStatement.bind(planOps));
    this.capabilities.set('update_goal_statement', planOps.handleUpdateGoalStatement.bind(planOps));
    this.capabilities.set('update_target_statement', planOps.handleUpdateTargetStatement.bind(planOps));
    this.capabilities.set('update_deliverables', planOps.handleUpdateDeliverables.bind(planOps));
    this.capabilities.set('update_metrics', planOps.handleUpdateMetrics.bind(planOps));
    this.capabilities.set('update_stakeholders', planOps.handleUpdateStakeholders.bind(planOps));
    this.capabilities.set('update_programs', planOps.handleUpdatePrograms.bind(planOps));
    this.capabilities.set('update_design_challenge', planOps.handleUpdateDesignChallenge.bind(planOps));

    console.log(`🎯 CapabilityRegistry: Strategy update capabilities registered:`, [
      'update_summary', 'update_problem_statement', 'update_goal_statement', 
      'update_target_statement', 'update_objectives', 'update_deliverables', 
      'update_metrics', 'update_stakeholders', 'update_design_challenge'
    ]);

    // Strategy Component Updates - generic handler that routes to specific handlers
    this.capabilities.set('update_strategy_component', planOps.handleUpdateStrategyComponent.bind(planOps));

    // Conversation Mode capabilities
    this.capabilities.set('generate_plan_cta', planOps.handleGeneratePlanCta.bind(planOps));
    this.capabilities.set('refine_brief', planOps.handleRefineBrief.bind(planOps));
    this.capabilities.set('suggest_approach', planOps.handleSuggestApproach.bind(planOps));

    console.log(`🎯 CapabilityRegistry: Conversation capabilities registered:`, [
      'generate_plan_cta', 'refine_brief', 'suggest_approach'
    ]);

    // Product Strategy capabilities
    const productStrategy = new ProductStrategyCapability(planStateSetters, planGetters, savePlan);
    this.capabilities.set('update_design_vision', productStrategy.handleUpdateDesignVision.bind(productStrategy));
    this.capabilities.set('update_strategic_themes', productStrategy.handleUpdateStrategicThemes.bind(productStrategy));
    this.capabilities.set('update_opportunity_areas', productStrategy.handleUpdateOpportunityAreas.bind(productStrategy));
    this.capabilities.set('update_audience_insights', productStrategy.handleUpdateAudienceInsights.bind(productStrategy));
    this.capabilities.set('update_design_hypotheses', productStrategy.handleUpdateDesignHypotheses.bind(productStrategy));
    this.capabilities.set('update_brief_deconstruction', productStrategy.handleUpdateBriefDeconstruction.bind(productStrategy));
    this.capabilities.set('update_information_assessment', productStrategy.handleUpdateInformationAssessment.bind(productStrategy));
    this.capabilities.set('update_strategic_guidance', productStrategy.handleUpdateStrategicGuidance.bind(productStrategy));
    this.capabilities.set('update_experience_design_principles', productStrategy.handleUpdateExperienceDesignPrinciples.bind(productStrategy));
    this.capabilities.set('update_success_metrics', productStrategy.handleUpdateSuccessMetrics.bind(productStrategy));
    this.capabilities.set('update_unmet_needs', productStrategy.handleUpdateUnmetNeeds.bind(productStrategy));
    this.capabilities.set('update_competitive_ux_positioning', productStrategy.handleUpdateCompetitiveUxPositioning.bind(productStrategy));

    console.log(`🎯 CapabilityRegistry: Product Strategy capabilities registered:`, [
      'update_design_vision', 'update_strategic_themes', 'update_opportunity_areas', 'update_audience_insights',
      'update_design_hypotheses', 'update_brief_deconstruction', 'update_information_assessment', 'update_strategic_guidance',
      'update_experience_design_principles', 'update_success_metrics', 'update_unmet_needs', 'update_competitive_ux_positioning'
    ]);

    // Competitor Analysis capabilities
    const competitorAnalysis = new CompetitorAnalysisCapability(planStateSetters, planGetters, savePlan);
    this.capabilities.set('update_market_intelligence', competitorAnalysis.handleUpdateMarketIntelligence.bind(competitorAnalysis));
    this.capabilities.set('update_opportunity_analysis', competitorAnalysis.handleUpdateOpportunityAnalysis.bind(competitorAnalysis));
    this.capabilities.set('update_methodology', competitorAnalysis.handleUpdateMethodology.bind(competitorAnalysis));

    console.log(`🎯 CapabilityRegistry: Competitor Analysis capabilities registered:`, [
      'update_market_intelligence', 'update_opportunity_analysis', 'update_methodology'
    ]);

    console.log(`🎯 CapabilityRegistry: Initialized ${this.capabilities.size} capabilities`);
  }

  hasCapability(capabilityName) {
    return this.capabilities.has(capabilityName);
  }

  async executeCapability(capabilityName, data, action = null, context = {}) {
    const capability = this.capabilities.get(capabilityName);
    if (!capability) {
      throw new Error(`Unknown capability: ${capabilityName}`);
    }

    console.log(`🔧 CapabilityRegistry: Executing ${capabilityName} with data:`, data);
    console.log(`🔧 CapabilityRegistry: Action context:`, action);

    try {
      // CRITICAL FIX: Ensure action context includes capability ID for proper routing
      const enhancedAction = {
        ...action,
        capability: capabilityName,
        capabilityId: capabilityName
      };

      // Route to the appropriate capability handler
      const result = await capability(data, enhancedAction, context);
      console.log(`✅ CapabilityRegistry: ${capabilityName} completed:`, result);
      return result;
    } catch (error) {
      console.error(`❌ CapabilityRegistry: ${capabilityName} failed:`, error);
      throw error;
    }
  }

  getAvailableCapabilities() {
    return Array.from(this.capabilities.keys());
  }

  // Get information about all capabilities
  getCapabilityInfo() {
    const info = {};
    for (const [name, capability] of this.capabilities.entries()) {
      info[name] = {
        methods: Object.getOwnPropertyNames(Object.getPrototypeOf(capability))
          .filter(name => name !== 'constructor' && typeof capability[name] === 'function')
      };
    }
    return info;
  }

  // Get a specific capability instance
  getCapability(capabilityName) {
    return this.capabilities.get(capabilityName);
  }

  // Get the PlanOperationsCapability instance directly
  getPlanOperationsInstance() {
    return this.planOperationsInstance;
  }
}

module.exports = CapabilityRegistry;